import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SiteModeScreen from './components/SiteModeScreen';
import { useSiteMode } from './hooks/useSiteMode';
import { LanguageProvider } from './i18n/LanguageContext';

// Layouts
import MainLayout from './layouts/MainLayout';

// Lazy loaded pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));

// Loading component
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-primary-500"></div>
  </div>
);

const SiteModeGate = () => {
  const { mode, message, loading, isRestricted } = useSiteMode();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-primary-500"></div>
      </div>
    );
  }

  if (isRestricted) {
    return <SiteModeScreen mode={mode === 'maintenance' ? 'maintenance' : 'coming_soon'} message={message} />;
  }

  return <RouterProvider router={router} />;
};

// Router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <MainLayout>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'terms',
        element: <TermsOfUsePage />,
      },
      {
        path: 'account-deletion',
        element: <AccountDeletionPage />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <SiteModeGate />
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App; 
